import React, { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Input, Modal } from '../../../../../components';
import api from '../../../../../utils/api';
import { copyToClipboard } from '../../../../../utils/copyToClipboard';

interface VerifyDnsModalProps {
  isOpen: boolean;
  agentUserId: string;
  /**
   * Fires with the updated verification row after a successful
   * check so the parent can refresh its status list without a
   * full refetch. Also called on modal close.
   */
  onClose: (updated?: VerificationRow) => void;
}

interface VerificationRow {
  id: string;
  type: 'email' | 'endpoint' | 'dns_txt';
  targetValue: string;
  status: 'pending' | 'verified' | 'stale' | 'revoked';
  verifiedAt: string | null;
}

interface StartResponse {
  verification: VerificationRow;
  token: string;
  recordName: string;
  recordValue: string;
  reissued: boolean;
  instructions: string;
}

/**
 * Owner flow for ADR-012 DNS TXT verification (#586). Same two-step
 * shape as VerifyEndpointModal, adapted for DNS:
 *
 *   1. Owner enters hostname (foo.com or api.foo.com)
 *   2. Server returns recordName + recordValue the owner adds to
 *      their DNS zone as a TXT record
 *   3. Owner runs check; server does dns.resolveTxt(recordName)
 *      + matches value; badge activates
 *
 * Failure keeps the row pending; owner adjusts DNS + retries check
 * without another initiate — same pattern as endpoint verify.
 */
export const VerifyDnsModal: React.FC<VerifyDnsModalProps> = ({
  isOpen,
  agentUserId,
  onClose,
}) => {
  const [hostname, setHostname] = useState('');
  const [initiateState, setInitiateState] = useState<
    'idle' | 'submitting' | 'ready' | 'error'
  >('idle');
  const [initiateError, setInitiateError] = useState<string | null>(null);
  const [start, setStart] = useState<StartResponse | null>(null);

  const [checkState, setCheckState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [checkError, setCheckError] = useState<string | null>(null);
  const [verified, setVerified] = useState<VerificationRow | null>(null);

  const reset = () => {
    setHostname('');
    setInitiateState('idle');
    setInitiateError(null);
    setStart(null);
    setCheckState('idle');
    setCheckError(null);
    setVerified(null);
  };

  const handleClose = () => {
    const updated = verified;
    reset();
    onClose(updated ?? undefined);
  };

  const handleInitiate = async (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = hostname.trim();
    if (!trimmed) {
      setInitiateError('Enter a hostname');
      setInitiateState('error');
      return;
    }
    setInitiateState('submitting');
    setInitiateError(null);
    try {
      const res = await api.post<StartResponse>(
        `/agents/mine/${agentUserId}/verify/dns`,
        { hostname: trimmed },
      );
      setStart(res.data);
      setInitiateState('ready');
    } catch (err: any) {
      const details = err?.response?.data?.details;
      const message =
        details?.hostname?._errors?.[0] ||
        err?.response?.data?.error ||
        'Failed to start verification';
      setInitiateError(message);
      setInitiateState('error');
    }
  };

  const handleCheck = async () => {
    if (!start) return;
    setCheckState('loading');
    setCheckError(null);
    try {
      const res = await api.post<{ verification: VerificationRow }>(
        `/agents/mine/${agentUserId}/verify/dns/${start.verification.id}/check`,
      );
      setVerified(res.data.verification);
      setCheckState('success');
      toast.success('Hostname verified');
    } catch (err: any) {
      const reason =
        err?.response?.data?.reason ||
        err?.response?.data?.error ||
        'Check failed';
      setCheckError(reason);
      setCheckState('error');
    }
  };

  const copyRecordName = async () => {
    if (!start) return;
    if (await copyToClipboard(start.recordName)) {
      toast.success('Record name copied');
    } else {
      toast.error('Copy failed — select and copy manually');
    }
  };
  const copyRecordValue = async () => {
    if (!start) return;
    if (await copyToClipboard(start.recordValue)) {
      toast.success('Record value copied');
    } else {
      toast.error('Copy failed — select and copy manually');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Verify DNS hostname"
      onClose={handleClose}
      size="md"
      footer={
        <div className="verify-endpoint-modal__footer">
          <Button variant="secondary" onClick={handleClose}>
            {checkState === 'success' ? 'Done' : 'Close'}
          </Button>
          {initiateState !== 'ready' && (
            <Button
              onClick={handleInitiate as any}
              disabled={initiateState === 'submitting'}
            >
              {initiateState === 'submitting' ? 'Issuing…' : 'Continue'}
            </Button>
          )}
          {initiateState === 'ready' && checkState !== 'success' && (
            <Button
              onClick={handleCheck}
              disabled={checkState === 'loading'}
            >
              {checkState === 'loading' ? 'Checking…' : 'Run check'}
            </Button>
          )}
        </div>
      }
    >
      {initiateState !== 'ready' ? (
        <form className="verify-endpoint-modal" onSubmit={handleInitiate}>
          <p className="verify-endpoint-modal__hint">
            Enter the bare hostname you want to prove you control (e.g.{' '}
            <code>foo.com</code> or <code>api.foo.com</code> — no protocol,
            path, or port). We'll return a TXT record to add to your DNS
            zone.
          </p>
          <Input
            label="Hostname"
            type="text"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            maxLength={253}
            required
            placeholder="foo.com"
            disabled={initiateState === 'submitting'}
            error={
              initiateState === 'error' ? initiateError ?? undefined : undefined
            }
          />
        </form>
      ) : (
        <div className="verify-endpoint-modal">
          <ol className="verify-endpoint-modal__steps">
            <li>
              <div>Add a <strong>TXT record</strong> to your DNS zone:</div>
              <div className="verify-endpoint-modal__code-row">
                <span className="verify-endpoint-modal__label">Name</span>
                <code className="verify-endpoint-modal__code">
                  {start?.recordName}
                </code>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={copyRecordName}
                >
                  Copy
                </Button>
              </div>
              <div className="verify-endpoint-modal__code-row">
                <span className="verify-endpoint-modal__label">Value</span>
                <code className="verify-endpoint-modal__code">
                  {start?.recordValue}
                </code>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={copyRecordValue}
                >
                  Copy
                </Button>
              </div>
            </li>
            <li>
              Wait a minute or two for propagation.{' '}
              <a
                href="https://dnschecker.org"
                target="_blank"
                rel="noreferrer"
              >
                dnschecker.org
              </a>{' '}
              is handy for confirming the record is live.
            </li>
            <li>
              Click <strong>Run check</strong>. Klyx resolves the record and
              matches the value against the challenge; badge activates on
              match.
            </li>
          </ol>

          {start?.reissued && (
            <p className="verify-endpoint-modal__reissued">
              Note: this challenge replaces any previous one for this
              hostname.
            </p>
          )}

          {checkState === 'error' && (
            <p className="verify-endpoint-modal__error">
              Check failed: {checkError}. Fix the TXT record and click Run
              check again — the challenge is unchanged.
            </p>
          )}

          {checkState === 'success' && verified && (
            <p className="verify-endpoint-modal__success">
              ✓ Verified{' '}
              {verified.verifiedAt
                ? `at ${new Date(verified.verifiedAt).toLocaleString()}`
                : ''}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default VerifyDnsModal;
